import { Injectable } from '@angular/core';
import { NotificationTypes } from '../models/NotificationTypes';
import { FilesService } from './files.service';
import { FirebaseUtilitiesService } from './firebase-utilities.service';
import { environment } from '../../environments/environment';

/**
 * Service for handling file-related business logic in ClientProfileComponent
 * Extracted to reduce component complexity and improve maintainability
 */
@Injectable({
  providedIn: 'root'
})
export class ClientProfileFileService {
  constructor(
    private filesService: FilesService,
    private firebaseUtilitiesService: FirebaseUtilitiesService
  ) {}

  /**
   * Gets files from INDEX folder
   * @param params - Parameters for fetching files
   * @returns Array of files from INDEX folder
   */
  async getFilesIndexFolder(params: {
    casename: string;
    roleConsultant: boolean;
    userOwnerID: string;
    userEmail?: string;
  }): Promise<any[]> {
    let files = (await this.filesService.getFilesByCaseName(params.casename, true, params.userOwnerID)).filter(
      ({ type }) => type !== 'folder'
    );

    if (params.roleConsultant && params.userEmail) {
      files = files
        .filter(({ ownerID }) => ownerID === params.userOwnerID)
        .filter(({ sharedUsers }) => sharedUsers && sharedUsers.length > 0)
        .filter(({ sharedUsers }) => sharedUsers.some(h => h.email === params.userEmail));
    }

    // Sort files by fileName
    files.sort((a, b) => {
      const aName = a.fileName || '';
      const bName = b.fileName || '';
      return aName.toLowerCase() > bName.toLowerCase() ? 1 : -1;
    });

    return files;
  }

  /**
   * Gets files from a specific folder
   * @param params - Parameters for fetching files
   * @returns Array of files from the folder
   */
  async getFilesFolder(params: {
    folderId: string;
    casename: string;
    email: string;
    ownerID: string;
    userOwnerID: string;
    isConsultant: boolean;
    caseFolders: any[];
  }): Promise<any[]> {
    // Fetch files and folders, merge and deduplicate by id
    const [directFiles, directFolders] = await Promise.all([
      this.filesService.getFilesByParentFolderId(
        params.folderId,
        params.casename,
        null,
        params.isConsultant,
        params.isConsultant ? params.email : '',
        params.ownerID,
        params.userOwnerID
      ),
      this.filesService.getFolderItemsByParentFolderId(
        params.folderId,
        params.casename,
        null,
        params.isConsultant,
        params.isConsultant ? params.email : '',
        params.ownerID,
        params.userOwnerID
      )
    ]);

    const filesMap = new Map<string, any>();
    [...directFiles, ...directFolders].forEach(item => {
      const key = item.folderId || item.fileId || item.id || Math.random().toString();
      filesMap.set(key, item);
    });

    const files = Array.from(filesMap.values()).filter(f => {
      const fileName = f.filename || f.fileName || f.name;
      return fileName && fileName.trim() !== '';
    });

    // Decorate files with additional metadata
    const filesArr = await Promise.all(
      files.map(async file => {
        file = this.getFileWithNotifications(file, params.caseFolders);
        if (file.ftype === 'Folder') {
          const folderExtra = await this.getFolderExtraX(
            file.fileName,
            file.ftype,
            file.folderId,
            file.predefined
          );
          return { ...file, ...folderExtra };
        } else {
          const { name, color } = this.getFoldersByParentFolderId(params.caseFolders, file.parentFolder);
          return {
            ...file,
            predefined: false,
            parentFolderName: name,
            color: color,
            hasNotifications: this.checkIfFileHasNotifications(file)
          };
        }
      })
    );

    // Sort by fileName
    filesArr.sort((a, b) => {
      const aName = a.fileName || '';
      const bName = b.fileName || '';
      return aName.toLowerCase() > bName.toLowerCase() ? 1 : -1;
    });

    return filesArr;
  }

  /**
   * Gets folder extra metadata
   * @param fileName - Name of the folder
   * @param ftype - Type of the folder
   * @param folderId - ID of the folder
   * @param predefined - Whether folder is predefined
   * @returns Folder extra metadata
   */
  async getFolderExtraX(fileName: string, ftype: string, folderId: string, predefined: boolean): Promise<any> {
    return {
      creator: '', // Will be set by component
      fileDesc: '',
      fileId: '', // Will be set by component
      fileName: `${fileName}`,
      ftype: ftype,
      filePath: ``,
      name: `${fileName} ${await this.firebaseUtilitiesService.getNumberOfFilesByFolderId(folderId)}`,
      parentFolderName: fileName,
      notes: [],
      sharedUsers: [],
      storename: '',
      viewerurl: '',
      predefined: predefined || false
    };
  }

  /**
   * Gets folders by parent folder ID
   * @param caseFolders - Array of case folders
   * @param parentFolderId - Parent folder ID
   * @returns Folder element with name and color
   */
  getFoldersByParentFolderId(caseFolders: any[], parentFolderId: string): { name: string; color: string } {
    const filtered = caseFolders.filter(({ folderId }) => folderId === parentFolderId)[0];
    if (!filtered) {
      return {
        name: 'top',
        color: 'pink'
      };
    }
    return {
      name: filtered.name || 'top',
      color: filtered.color || 'pink'
    };
  }

  /**
   * Checks if a file has notifications
   * @param file - File to check
   * @returns True if file has notifications
   */
  checkIfFileHasNotifications(file: any): boolean {
    return [
      'modifiedNoteNotifications',
      'newFileNotifications',
      'newNoteNotifications',
      'sharedFileNotifications'
    ].some(key => Array.isArray(file[key]) && file[key].length > 0);
  }

  /**
   * Gets file with notifications attached
   * @param file - File to process
   * @param caseFolders - Case folders for parent folder lookup
   * @param getUserDataNotifications - Function to get user data notifications
   * @returns File with notifications
   */
  getFileWithNotifications(
    file: any,
    caseFolders: any[],
    getUserDataNotifications?: (fileId: string) => any[]
  ): any {
    const notifications = getUserDataNotifications ? getUserDataNotifications(file.fileId) : [];
    const categorizedNotifications = this.getCategorizedNotifications(notifications);
    return {
      ...file,
      ...categorizedNotifications
    };
  }

  /**
   * Categorizes notifications by type
   * @param notifications - Array of notifications
   * @returns Categorized notifications object
   */
  getCategorizedNotifications(notifications: { type: NotificationTypes }[]): any {
    if (environment.config.notifications === 0) {
      return {
        newNoteNotifications: [],
        newFileNotifications: [],
        modifiedNoteNotifications: [],
        sharedFileNotifications: []
      };
    }
    return {
      newNoteNotifications: notifications.filter(n => n.type === NotificationTypes.newnote),
      newFileNotifications: notifications.filter(n => n.type === NotificationTypes.newfile),
      modifiedNoteNotifications: notifications.filter(n => n.type === NotificationTypes.modifiednote),
      sharedFileNotifications: notifications.filter(n => n.type === NotificationTypes.sharedfile)
    };
  }

  /**
   * Finds missing items in array1 that are not in array2
   * @param arr1 - First array
   * @param arr2 - Second array
   * @returns Array of missing items
   */
  findIn(arr1: string[], arr2: string[]): string[] {
    const notFound: string[] = [];
    arr1.forEach(e => {
      if (!arr2.includes(e)) {
        notFound.push(e);
      }
    });
    return notFound;
  }

  /**
   * Sorts files by fileName
   * @param files - Array of files to sort
   * @returns Sorted array of files
   */
  sortFilesByFileName(files: any[]): any[] {
    return files.sort((a, b) => {
      const aName = a.fileName || '';
      const bName = b.fileName || '';
      return aName.toLowerCase() > bName.toLowerCase() ? 1 : -1;
    });
  }
}
